import { Skeleton, Stack } from '@mantine/core';
import { randomId } from '@mantine/hooks';
import { DeviceGetResponse } from 'api/actions/device-get/device-get-response';
import { useApi } from 'api/api-context';
import DeviceFiles from 'components/forms/device/DeviceFiles';
import { DeviceFile } from 'components/forms/device/DeviceForm';
import panic from 'errors/panic';
import { DEVICE_FILE_TYPE_NAME_MAP } from 'model/DeviceFileType';
import { DeviceFileType } from 'model/DeviceFileType';
import { useEffect, useMemo, useState } from 'react';

interface ParentDeviceFilesProps {
  parentDeviceId: string;
}

/**
 * Displays readonly files configured on parent device.
 */
export default function ParentDeviceFiles({ parentDeviceId }: ParentDeviceFilesProps) {
  const { getAction } = useApi();
  const [parentDevice, setParentDevice] = useState<DeviceGetResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getDevice = getAction('DeviceGet');

    setLoading(true);

    getDevice({
      parameters: { deviceId: parentDeviceId },
    })
      .then((device) => {
        setParentDevice(device);
      })
      .catch(panic)
      .finally(() => {
        setLoading(false);
      });
  }, [getAction, parentDeviceId]);

  const files = useMemo<DeviceFile[]>(() => {
    return (parentDevice?.files ?? [])
      .filter((file) => file.fileType in DEVICE_FILE_TYPE_NAME_MAP)
      .map((file) => ({
        deviceFileId: file.deviceFileId,
        fileId: file.fileId,
        fileName: file.fileName,
        originalFileName: '',
        uploadedAt: file.created ? new Date(file.created) : new Date(),
        key: randomId(),
        deleted: false,
        isMainImage: file.isMainImage,
        hasKepSignature: file.hasKepSignature,
        type: file.fileType as DeviceFileType,
      }));
  }, [parentDevice?.files]);

  if (loading) {
    return (
      <Stack spacing={16}>
        <Skeleton height={80} radius="sm" />
        <Skeleton height={170} radius="sm" />
      </Stack>
    );
  }

  return (
    <DeviceFiles
      context="parent-files"
      deviceId={Number(parentDeviceId)}
      deviceName={parentDevice?.deviceName ?? ''}
      departmentId={parentDevice?.department?.departmentId}
      files={files}
      readonly
    />
  );
}
